import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone, Mail, MapPin, User, Users, Activity, Calendar, MessageSquare,
  Clock, Zap, Globe, AlertCircle, ChevronDown, Star, Facebook, Instagram,
  Linkedin, Twitter, Youtube
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { contactTranslations } from '../translations/contact';
import { useManagedContent } from '../hooks/useManagedContent';
import { useManagedImages } from '../hooks/useManagedImages';
import { pageImageConfig } from '../content/pageImageConfig';
import '../App.css';

const sportValues = ['Basketball', 'Volleyball', 'Swimming', 'Water Polo', 'Athletics', 'Football', 'Multi-Sport Camp', 'Other'];
const locationValues = ['Sofia - Mladost', 'Sofia - NSA', 'Sofia - Boyana', 'Plovdiv', 'Kranevo', 'Not sure yet'];
const hearAboutValues = ['Google Search', 'Social Media', 'Referral', 'Previous Client', 'Sports Organization', 'Other'];

function Contact() {
  const { language } = useLanguage();
  const t = useManagedContent({
    pageKey: 'contact',
    language,
    defaultTranslations: contactTranslations
  });
  const images = useManagedImages({
    pageKey: 'contact',
    defaultImages: pageImageConfig.contact
  });

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    sport: '',
    location: '',
    participants: '',
    dates: '',
    message: '',
    hearAbout: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = t.form.errorFullName;
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t.form.errorEmail;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t.form.errorEmailInvalid;
    }
    
    if (!formData.sport) {
      newErrors.sport = t.form.errorSport;
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t.form.errorMessage;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        organization: '',
        sport: '',
        location: '',
        participants: '',
        dates: '',
        message: '',
        hearAbout: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 1500);
  };

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const faqs = t.faq.items;
  const testimonials = t.testimonials.items.map((x) => ({ ...x, rating: 5 }));

  return (
    <div className="App">
      <Header />
      
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-background">
          <img 
            src={images.hero}
            alt="Contact us"
            loading="eager"
          />
        </div>
        <div className="contact-hero-overlay"></div>
        <div className="contact-hero-content">
          <motion.div 
            className="contact-hero-text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <nav className="breadcrumb">
              <Link to="/">{t.hero.home}</Link>
              <span> / </span>
              <span>{t.hero.contact}</span>
            </nav>
            <h1 className="contact-hero-heading">{t.hero.heading}</h1>
            <h2 className="contact-hero-subheading">{t.hero.subheading}</h2>
            <p className="contact-hero-tagline">{t.hero.tagline}</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods - Quick Access */}
      <section className="contact-methods-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.methods.heading}
          </motion.h2>
          
          <div className="contact-methods-grid">
            <motion.div
              className="contact-method-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="contact-method-icon">
                <Phone size={48} color="#C49818" />
              </div>
              <h3>{t.methods.callUs}</h3>
              <p className="contact-method-primary">+359 88 539 7102</p>
              <p className="contact-method-secondary">{t.methods.hours1}</p>
              <p className="contact-method-secondary">{t.methods.hours2}</p>
              <motion.a
                href="tel:+359885397102"
                className="contact-method-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t.methods.callNow}
              </motion.a>
            </motion.div>

            <motion.div
              className="contact-method-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -5 }}
            >
              <div className="contact-method-icon">
                <Mail size={48} color="#C49818" />
              </div>
              <h3>{t.methods.emailUs}</h3>
              <p className="contact-method-primary">info@championmaker.org</p>
              <p className="contact-method-secondary">{t.methods.respond24}</p>
              <motion.a
                href="mailto:info@championmaker.org"
                className="contact-method-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t.methods.sendEmail}
              </motion.a>
            </motion.div>

            <motion.div
              className="contact-method-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              whileHover={{ y: -5 }}
            >
              <div className="contact-method-icon">
                <MapPin size={48} color="#C49818" />
              </div>
              <h3>{t.methods.visitUs}</h3>
              <p className="contact-method-primary">{t.methods.sofiaBulgaria}</p>
              <p className="contact-method-secondary">{t.methods.byAppointment}</p>
              <motion.a
                href="https://maps.google.com/?q=Sofia,Bulgaria"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-method-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t.methods.getDirections}
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Contact Form */}
      <section className="contact-form-section">
        <div className="container">
          <motion.div
            className="contact-form-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-heading">{t.form.heading}</h2>
            <p className="section-subheading">{t.form.subheading}</p>
          </motion.div>

          <div className="contact-form-layout">
            {/* Left Column - Form */}
            <motion.div
              className="contact-form-wrapper"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <form onSubmit={handleSubmit} className="contact-form">
                {submitSuccess && (
                  <motion.div
                    className="form-success-message"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    ✓ {t.form.successMessage}
                  </motion.div>
                )}

                <div className="form-group">
                  <label htmlFor="fullName">{t.form.fullName} <span className="required">*</span></label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder={t.form.placeholderName}
                      className={errors.fullName ? 'error' : ''}
                    />
                  </div>
                  {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t.form.email} <span className="required">*</span></label>
                  <div className="input-wrapper">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t.form.placeholderEmail}
                      className={errors.email ? 'error' : ''}
                    />
                  </div>
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">{t.form.phone}</label>
                  <div className="input-wrapper">
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t.form.placeholderPhone}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="organization">{t.form.organization}</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder={t.form.placeholderOrg}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="sport">{t.form.sport} <span className="required">*</span></label>
                  <div className="input-wrapper">
                    <select
                      id="sport"
                      name="sport"
                      value={formData.sport}
                      onChange={handleInputChange}
                      className={errors.sport ? 'error' : ''}
                    >
                      <option value="">{t.form.selectSport}</option>
                      {sportValues.map((v, i) => (
                        <option key={v} value={v}>{t.form.sportOptions[i]}</option>
                      ))}
                    </select>
                  </div>
                  {errors.sport && <span className="error-message">{errors.sport}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="location">{t.form.location}</label>
                  <div className="input-wrapper">
                    <select
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                    >
                      <option value="">{t.form.selectLocation}</option>
                      {locationValues.map((v, i) => (
                        <option key={v} value={v}>{t.form.locationOptions[i]}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="participants">{t.form.participants}</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      id="participants"
                      name="participants"
                      value={formData.participants}
                      onChange={handleInputChange}
                      placeholder={t.form.placeholderParticipants}
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="dates">{t.form.dates}</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="dates"
                      name="dates"
                      value={formData.dates}
                      onChange={handleInputChange}
                      placeholder={t.form.placeholderDates}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t.form.message} <span className="required">*</span></label>
                  <div className="textarea-wrapper">
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder={t.form.placeholderMessage}
                      rows="6"
                      className={errors.message ? 'error' : ''}
                    />
                  </div>
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="hearAbout">{t.form.hearAbout}</label>
                  <div className="input-wrapper">
                    <select
                      id="hearAbout"
                      name="hearAbout"
                      value={formData.hearAbout}
                      onChange={handleInputChange}
                    >
                      <option value="">{t.form.selectHearAbout}</option>
                      {hearAboutValues.map((v, i) => (
                        <option key={v} value={v}>{t.form.hearAboutOptions[i]}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  className="form-submit-btn"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                >
                  {isSubmitting ? t.form.sending : t.form.sendMessage}
                </motion.button>

                <p className="form-required-note">{t.form.requiredNote}</p>
              </form>
            </motion.div>

            {/* Right Column - Info Cards */}
            <motion.div
              className="contact-info-cards"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="info-card">
                <div className="info-card-header">
                  <Clock size={24} color="#C49818" />
                  <h3>{t.infoCards.officeHours}</h3>
                </div>
                <div className="info-card-content">
                  <p>{t.infoCards.mondayFriday}</p>
                  <p>{t.infoCards.saturday}</p>
                  <p>{t.infoCards.sundayClosed}</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-header">
                  <Zap size={24} color="#C49818" />
                  <h3>{t.infoCards.quickResponse}</h3>
                </div>
                <div className="info-card-content">
                  <p>{t.infoCards.quickResponseText}</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-header">
                  <Globe size={24} color="#C49818" />
                  <h3>{t.infoCards.languages}</h3>
                </div>
                <div className="info-card-content">
                  {t.infoCards.langList.map((lang, i) => (
                    <p key={i}>{lang}</p>
                  ))}
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-header">
                  <AlertCircle size={24} color="#C49818" />
                  <h3>{t.infoCards.urgentInquiries}</h3>
                </div>
                <div className="info-card-content">
                  <p>{t.infoCards.urgentText} <strong>+359 88 539 7102</strong></p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="contact-map-section">
        <div className="container">
          <motion.h2
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.map.heading}
          </motion.h2>
          <div className="map-wrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2933.5!2d23.3219!3d42.6975!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDQxJzUxLjAiTiAyM8KwMTknMTguOCJF!5e0!3m2!1sen!2sbg!4v1234567890"
              width="100%"
              height="500"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Champion Maker Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="contact-faq-section">
        <div className="container">
          <motion.div
            className="faq-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-heading">{t.faq.heading}</h2>
            <p className="section-subheading">{t.faq.subheading}</p>
          </motion.div>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className={`faq-item ${expandedFAQ === index ? 'expanded' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <button
                  className="faq-question"
                  onClick={() => toggleFAQ(index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown
                    size={24}
                    className={`faq-chevron ${expandedFAQ === index ? 'rotated' : ''}`}
                  />
                </button>
                <motion.div
                  className="faq-answer"
                  initial={false}
                  animate={{
                    height: expandedFAQ === index ? 'auto' : 0,
                    opacity: expandedFAQ === index ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <p>{faq.answer}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="contact-social-section">
        <div className="container">
          <motion.div
            className="social-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-heading">{t.social.heading}</h2>
            <p className="section-subheading">{t.social.subheading}</p>
          </motion.div>

          <div className="social-icons-grid">
            <motion.a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon-link"
              whileHover={{ scale: 1.1, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Facebook size={48} color="#C49818" />
            </motion.a>
            <motion.a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon-link"
              whileHover={{ scale: 1.1, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Instagram size={48} color="#C49818" />
            </motion.a>
            <motion.a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon-link"
              whileHover={{ scale: 1.1, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Linkedin size={48} color="#C49818" />
            </motion.a>
            <motion.a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon-link"
              whileHover={{ scale: 1.1, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Twitter size={48} color="#C49818" />
            </motion.a>
            <motion.a
              href="https://youtube.com"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon-link"
              whileHover={{ scale: 1.1, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Youtube size={48} color="#C49818" />
            </motion.a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="contact-testimonials-section">
        <div className="container">
          <motion.h2
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.testimonials.heading}
          </motion.h2>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} color="#C49818" fill="#C49818" />
                  ))}
                </div>
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <div className="testimonial-author">
                  <p className="testimonial-name">{testimonial.name}</p>
                  <p className="testimonial-org">{testimonial.organization}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="contact-cta-section">
        <div className="container">
          <motion.div
            className="contact-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>{t.cta.heading}</h2>
            <p>{t.cta.text}</p>
            <div className="contact-cta-buttons">
              <Link to="/packages">
                <motion.button
                  className="btn-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.cta.viewPackages}
                </motion.button>
              </Link>
              <Link to="/book">
                <motion.button
                  className="btn-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.cta.scheduleCall}
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Contact;
